/**
 * RenderingDiagnosticPanelContent — kit tool `renderingDiag`. Shows the status
 * of every step in the rendering pipeline; when the screen is black this tells
 * you exactly WHY. Polls ServiceRegistry / MapRenderer / live scene state.
 *
 * Zero-prop: player comes from useGame(), level from MapSystem (same data the
 * HUD fed it before the kit migration). The 🛠 kit's ToolWindow provides the
 * window chrome, so this renders the checkpoint body directly.
 */

import React from 'react';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { MapRenderer } from '../../graphics/MapRenderer';
import { useFps } from '../../diagnostics/useFps';
import { useGame } from '../contexts/GameContext';
import { DiagnosticCheckpointBody, DiagnosticCheckpoint } from '../components/DiagnosticCheckpointBody';
import { livePlayerPosition } from '../../systems/player/livePlayerPosition';

export const RenderingDiagnosticPanelContent: React.FC = () => {
  const { player } = useGame();

  // Real frame rate via the rAF hook. Scene presence comes from the live
  // SceneRegistry scene (window.__currentScene set by SceneSetupSystem == GameScene
  // active). Don't read constructor.name — prod minifies it.
  const fps = useFps();
  const sceneName = (window as any).__currentScene ? 'GameScene' : 'none';
  const infoString = `FPS: ${fps} | Scene: ${sceneName}`;

  const getCheckpoints = (): DiagnosticCheckpoint[] => {
    const registry = ServiceRegistry.getInstance();
    let mapRenderer: MapRenderer | null = null;
    try {
      mapRenderer = MapRenderer.getInstance();
    } catch { /* MapRenderer may not exist yet */ }

    const currentScene = (window as any).__currentScene as any | undefined;
    const phaserScene = currentScene;

    // Level id sourced live from MapSystem (was a prop fed by HUD).
    let levelId: string | undefined;
    try {
      const mapSystem = currentScene?.setup?.kernel?.getSystem?.('MapSystem') || registry.get('MapSystem');
      levelId = mapSystem?.getCurrentLevel?.()?.id;
    } catch { /* MapSystem may not exist yet */ }

    return [
      // --- Kernel Layer ---
      {
        label: 'GameBootstrap State',
        category: 'kernel',
        check: () => {
          const boot = (window as any).__GAME_BOOT__;
          if (!boot) return 'init() never called';
          if (boot.error) return `ERROR: ${boot.error}`;
          const steps = [
            boot.initCalled && 'init',
            boot.gameCreated && 'game',
            boot.scenesAdded && 'scenes',
            boot.readyFired && 'ready',
            boot.sceneStarted && 'started',
          ].filter(Boolean);
          return steps.join(' → ') || 'init called but nothing completed';
        },
      },
      {
        // Action-bar plan phase 1: why a slot shows ✨ is one look away.
        label: 'Ability loader',
        category: 'kernel',
        check: () => {
          const statuses = (window as any).__GAME_BOOT__?.abilityStatus as
            Record<string, { source: string; reason?: string }> | undefined;
          if (!statuses) return 'Not booted';
          const all = Object.entries(statuses);
          const count = (source: string) => all.filter(([, s]) => s.source === source).length;
          const failed = all.filter(([, s]) => s.source === 'failed').map(([id, s]) => `${id} (${s.reason})`);
          const summary = `${count('authored')} authored, ${count('toml')} TOML`;
          return failed.length > 0 ? `${summary}; FAILED: ${failed.join('; ')}` : summary;
        },
      },
      {
        label: 'GameKernel',
        category: 'kernel',
        check: () => {
          const scene = (window as any).__currentScene;
          const kernel = scene?.setup?.kernel;
          if (!kernel) return 'Not found (GameScene.create may not have run)';
          if (kernel.getIsBooted()) {
            const ids = kernel.getSystemIds?.() || [];
            return `Booted (${ids.length} systems: ${ids.slice(0, 5).join(', ')}${ids.length > 5 ? '...' : ''})`;
          }
          return 'Created but not booted';
        },
      },
      {
        label: 'Kernel Systems',
        category: 'kernel',
        check: () => {
          const scene = (window as any).__currentScene;
          const kernel = scene?.setup?.kernel;
          if (!kernel) return false;
          const ids = kernel.getSystemIds?.() || [];
          return ids.length > 0 ? ids.join(', ') : 'None registered';
        },
      },

      // --- Map Layer ---
      {
        label: 'MapSystem',
        category: 'map',
        check: () => {
          const scene = (window as any).__currentScene;
          const kernel = scene?.setup?.kernel;
          const kernelMap = kernel?.getSystem?.('MapSystem');
          if (kernelMap) return 'In Kernel';
          if (registry.has('MapSystem')) return 'In ServiceRegistry only (kernel missing it)';
          return false;
        },
      },
      {
        label: 'Level Data Loaded',
        category: 'map',
        check: () => {
          try {
            const scene = (window as any).__currentScene;
            const kernel = scene?.setup?.kernel;
            const kernelMap = kernel?.getSystem?.('MapSystem');
            const mapSystem = kernelMap || registry.get('MapSystem');
            if (mapSystem) {
              const currentLevel = mapSystem.getCurrentLevel?.();
              if (currentLevel) {
                return `${currentLevel.name} (${currentLevel.platforms?.length || 0} platforms)`;
              }
              return 'MapSystem exists but no level loaded';
            }
            return false;
          } catch {
            return false;
          }
        },
      },
      {
        label: 'Target Level ID',
        category: 'map',
        check: () => levelId || false,
      },

      // --- Render Layer ---
      {
        label: 'Platforms Rendered',
        category: 'render',
        check: () => {
          try {
            const visuals = mapRenderer?.getPlatformVisuals() || [];
            return visuals.length > 0 ? `${visuals.length} platform sprites` : false;
          } catch { return false; }
        },
      },
      {
        label: 'Active Scene',
        category: 'render',
        check: () => (currentScene ? 'GameScene active' : 'No active scene (SceneRegistry idle)'),
      },
      {
        label: 'GameScene Status',
        category: 'render',
        check: () => (currentScene ? 'Active & Running' : 'No active scene'),
      },
      {
        label: 'Camera Position',
        category: 'camera',
        check: () => {
          try {
            if (phaserScene?.cameras?.main) {
              const cam = phaserScene.cameras.main;
              return `(${Math.round(cam.scrollX)}, ${Math.round(cam.scrollY)}) zoom: ${cam.zoom.toFixed(2)}`;
            }
          } catch { /* scene may be mid-transition */ }
          return false;
        },
      },
      {
        label: 'Camera World Bounds',
        category: 'camera',
        check: () => {
          try {
            if (phaserScene?.cameras?.main) {
              const bounds = (phaserScene.cameras.main as any)._bounds;
              if (bounds && (bounds.width > 0 || bounds.height > 0)) {
                return `${bounds.width}x${bounds.height}`;
              }
              return 'Not set — camera may show black';
            }
          } catch { /* scene may be mid-transition */ }
          return false;
        },
      },

      // --- Player Layer ---
      {
        label: 'Player Entity',
        category: 'player',
        check: () => {
          if (player) {
            const at = livePlayerPosition(player.position);
            return `${player.name} at (${Math.round(at.x)}, ${Math.round(at.y)})`;
          }
          return false;
        },
      },
      {
        label: 'Player Sprite',
        category: 'player',
        check: () => {
          try {
            const scene = (window as any).__currentScene;
            const kernel = scene?.setup?.kernel;
            const entitySystem = kernel?.getSystem?.('EntitySystem');
            if (entitySystem) {
              const playerRenderer = entitySystem.getPlayerRenderer?.();
              if (playerRenderer) {
                const sprite = playerRenderer.getPlayerSprite?.();
                return sprite ? `Visible at (${Math.round(sprite.x)}, ${Math.round(sprite.y)})` : 'No sprite found';
              }
              return 'EntitySystem found but no PlayerRenderer';
            }
            return false;
          } catch {
            return false;
          }
        },
      },
    ];
  };

  const categories = [
    { id: 'kernel', label: '⚙️ Systems' },
    { id: 'map', label: '🗺️ Map' },
    { id: 'render', label: '🖼️ Rendering' },
    { id: 'camera', label: '📷 Camera' },
    { id: 'player', label: '👤 Player' },
  ];

  return (
    <DiagnosticCheckpointBody
      categories={categories}
      checkpoints={getCheckpoints()}
      infoString={infoString}
      pollIntervalMs={500}
    />
  );
};
